import React from "react";
import toast from "react-hot-toast";
import { Printer } from "react-feather";
import { PRINT_BASE, PRINT_TD, PRINT_TH, PrintHeader, printElement } from "../../../../components/common/printSheet";

/*
  Print one Stock Adjustment.

  Stock-In, Stock-Out, Return and LPO all print through StockDocumentPrintModal
  / StockDocumentPreview; Adjustment was the one detail page without a print at
  all (user, 2026-09-18: "give print option in the stock adjustment detail page
  also").

  It deliberately does NOT reuse StockDocumentPreview: that template always
  renders Price / Amount / VAT columns and a totals box, and an adjustment has
  no money on it at all - it would print a column of zeros and a KSh 0.00 total,
  which reads as a real figure. Adding a "hide the money" mode to that shared
  template would also mean touching a component five live document types depend
  on. So this is its own small sheet over the same shared popup plumbing.

  What an adjustment sheet is actually FOR: proving who changed stock, by how
  much, why, and who approved it - so the approval trail is on the page, not
  just the lines.
*/

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("en-GB") : "");
const fmtDateTime = (value) => (value ? new Date(value).toLocaleString("en-GB") : "");

const Row = ({ label, children }) => (
  <tr>
    <td style={{ ...PRINT_TD, width: "150px", fontWeight: 700 }}>{label}</td>
    <td style={PRINT_TD}>{children}</td>
  </tr>
);

export const StockAdjustmentSheet = ({ adj }) => {
  const isIncrease = adj.adjustmentType === "INCREASE";
  const section = (title) => (
    <div style={{ fontSize: "12px", fontWeight: 700, margin: "10px 0 4px", borderBottom: "1px solid #000" }}>
      {title}
    </div>
  );

  return (
    <div style={{ ...PRINT_BASE, padding: "6px" }}>
      <PrintHeader
        title={`STOCK ADJUSTMENT ${adj.adjustmentNo}`}
        subtitle={`${isIncrease ? "Increase" : "Decrease"} · ${adj.status}`}
        lines={[
          adj.isDeleted ? "*** DELETED ***" : null,
          adj.status === "SUBMITTED" ? "Not yet approved - stock balance is unchanged" : null,
        ]}
        branchCode={adj.branchCode}
      />

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Row label="Branch">{adj.branchCode}</Row>
          <Row label="Date">{fmtDate(adj.adjustmentDate)}</Row>
          <Row label="Type">{isIncrease ? "Increase (stock added)" : "Decrease (stock removed)"}</Row>
          <Row label="Reason">{adj.reason || "—"}</Row>
        </tbody>
      </table>

      {section(`Items (${adj.lines.length})`)}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...PRINT_TH, width: "30px" }}>#</th>
            <th style={PRINT_TH}>Product</th>
            <th style={{ ...PRINT_TH, width: "90px", textAlign: "right" }}>Adjusted Qty</th>
            <th style={{ ...PRINT_TH, width: "70px" }}>Unit</th>
            <th style={PRINT_TH}>Reason</th>
          </tr>
        </thead>
        <tbody>
          {adj.lines.map((l, idx) => (
            <tr key={l.lineId}>
              <td style={PRINT_TD}>{idx + 1}</td>
              <td style={PRINT_TD}>{l.productName}</td>
              {/* Signed, so a printed decrease can never be read as an increase
                  once it is off the screen that had the type in its header. */}
              <td style={{ ...PRINT_TD, textAlign: "right", fontWeight: 700 }}>
                {isIncrease ? "+" : "-"}
                {l.adjustedQty}
              </td>
              <td style={PRINT_TD}>{l.unit}</td>
              <td style={PRINT_TD}>{l.reason || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {section("Trail")}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <Row label="Created by">
            {adj.createdByName} · {fmtDateTime(adj.createdDate)}
          </Row>
          {adj.approvedByName && (
            <Row label="Approved by">
              {adj.approvedByName} · {fmtDateTime(adj.approvedDate)}
            </Row>
          )}
          {adj.status === "REJECTED" && (
            <Row label="Rejected by">
              {adj.rejectedByName}
              {adj.rejectionReason ? ` · ${adj.rejectionReason}` : ""}
            </Row>
          )}
          {adj.isDeleted && (
            <Row label="Deleted by">
              {adj.deletedByName} · {fmtDateTime(adj.deletedDate)}
              {adj.deleteReason ? ` · ${adj.deleteReason}` : ""}
            </Row>
          )}
        </tbody>
      </table>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "26px" }}>
        <tbody>
          <tr>
            {["Prepared By", "Approved By", "Store In-Charge"].map((label) => (
              <td key={label} style={{ ...PRINT_TD, borderBottom: "none", width: "33%" }}>
                <div style={{ borderTop: "1px solid #666", paddingTop: "3px", fontSize: "10px" }}>{label}</div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/** Opens the popup synchronously inside the click, or the blocker eats it. */
const StockAdjustmentPrintButton = ({ adj }) => {
  const print = () => {
    const win = window.open("", "_blank", "width=1000,height=900");
    if (!win) {
      toast.error("Allow pop-ups for this site to print.");
      return;
    }
    try {
      printElement(<StockAdjustmentSheet adj={adj} />, { title: adj.adjustmentNo, win });
    } catch (e) {
      win.close();
      toast.error(e?.message || "Could not prepare the print");
    }
  };

  return (
    <button
      type="button"
      className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
      onClick={print}
    >
      <Printer size={16} /> Print
    </button>
  );
};

export default StockAdjustmentPrintButton;
